import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { employerAPI, reviewsAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Building2, Users, Calendar, Plus, Loader2, User, Mail, Send,
  CheckCircle, Clock, FileText, AlertTriangle, MoreHorizontal, RefreshCw,
  Briefcase, CalendarDays, ChevronRight
} from 'lucide-react';

const statusConfig = {
  invited: { label: 'Invited', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Mail },
  signed_up: { label: 'Signed Up', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: User },
  record_pending: { label: 'Record Pending', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  verified: { label: 'Verified', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
};

const Company = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('staff');
  const [staff, setStaff] = useState([]);
  const [scheduledReviews, setScheduledReviews] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteData, setInviteData] = useState({
    name: '',
    email: '',
    job_title: '',
    department: '',
    start_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [staffRes, reviewsRes, companyRes] = await Promise.all([
        employerAPI.getStaff(),
        reviewsAPI.getScheduledReviews().catch(() => ({ data: [] })),
        employerAPI.getCompanyInfo()
      ]);
      setStaff(staffRes.data || []);
      setScheduledReviews(reviewsRes.data || []);
      setCompanyInfo(companyRes.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteData.name || !inviteData.email || !inviteData.job_title) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setInviteLoading(true);
    try {
      await employerAPI.inviteEmployee(inviteData);
      toast.success(`Invitation sent to ${inviteData.email}`);
      setShowInviteModal(false);
      setInviteData({ name: '', email: '', job_title: '', department: '', start_date: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvite = async (id) => {
    try {
      await employerAPI.resendInvitation(id);
      toast.success('Invitation resent!');
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="company-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E] flex items-center gap-2">
              <Building2 className="w-8 h-8 text-[#1B5AFF]" />
              {companyInfo?.company_name || 'Company'}
            </h1>
            <p className="mt-2 text-[#5B6472]">Manage your team and company profile</p>
          </div>
          <Button
            onClick={() => setShowInviteModal(true)}
            className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
            data-testid="add-employee-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-[#5B6472] text-xs">Total Staff</p>
                <p className="text-xl font-bold text-[#0A0F1E]">{staff.length}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-[#5B6472] text-xs">Verified</p>
                <p className="text-xl font-bold text-[#0A0F1E]">{staff.filter(s => s.status === 'verified').length}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-[#5B6472] text-xs">Pending</p>
                <p className="text-xl font-bold text-[#0A0F1E]">{staff.filter(s => ['invited', 'signed_up', 'record_pending'].includes(s.status)).length}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-[#5B6472] text-xs">Upcoming Reviews</p>
                <p className="text-xl font-bold text-[#0A0F1E]">{scheduledReviews.filter(r => r.status === 'scheduled').length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#F5F8FF] rounded-xl p-1">
            <TabsTrigger 
              value="staff" 
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg"
            >
              <Users className="w-4 h-4 mr-2" />
              Staff Overview
            </TabsTrigger>
            <TabsTrigger 
              value="reviews" 
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg"
            >
              <CalendarDays className="w-4 h-4 mr-2" />
              Scheduled Reviews
            </TabsTrigger>
            <TabsTrigger 
              value="profile" 
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Company Profile
            </TabsTrigger>
          </TabsList>

          {/* Staff Overview */}
          <TabsContent value="staff" className="mt-6">
            {staff.length === 0 ? (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <Users className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No team members yet</h3>
                <p className="text-[#5B6472] mb-6">Start by inviting employees to join your company on VETT.</p>
                <Button
                  onClick={() => setShowInviteModal(true)}
                  className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Employee
                </Button>
              </Card>
            ) : (
              <div className="space-y-3">
                {staff.map((member, i) => {
                  const status = statusConfig[member.status] || statusConfig.invited;
                  const hasProfile = member.employee_id || member.user_id;
                  return (
                    <Card 
                      key={member.id}
                      className={`bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 animate-fade-in transition-all ${hasProfile ? 'cursor-pointer hover:border-[#1B5AFF]/50' : ''}`}
                      style={{ animationDelay: `${i * 0.05}s` }}
                      data-testid={`staff-${member.id}`}
                      onClick={() => hasProfile && navigate(`/employer/staff/${member.employee_id || member.user_id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {member.photo_url ? (
                            <img src={member.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                              <User className="w-5 h-5 text-[#5B6472]" />
                            </div>
                          )}
                          <div>
                            <h3 className="font-medium text-[#0A0F1E]">{member.name}</h3>
                            <p className="text-[#5B6472] text-sm">{member.job_title}</p>
                            <p className="text-[#5B6472] text-xs">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${status.color}`}>
                            <status.icon className="w-3.5 h-3.5" />
                            {status.label}
                          </span>
                          {member.status === 'invited' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleResendInvite(member.invitation_id || member.id); }}
                              className="text-[#5B6472] hover:text-[#1B5AFF] rounded-lg"
                              data-testid={`resend-${member.id}`}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                          {hasProfile && (
                            <ChevronRight className="w-5 h-5 text-[#5B6472]" />
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Scheduled Reviews */}
          <TabsContent value="reviews" className="mt-6">
            {scheduledReviews.length === 0 ? (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <Calendar className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No scheduled reviews</h3>
                <p className="text-[#5B6472]">Schedule reviews from the Reviews page.</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {scheduledReviews.map((review, i) => {
                  const reviewTypeColors = {
                    'weekly': 'text-green-400',
                    'quarterly': 'text-amber-400',
                    'annual': 'text-red-400',
                    'one-time': 'text-blue-400',
                  };
                  return (
                    <Card 
                      key={review.id}
                      className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 animate-fade-in"
                      style={{ animationDelay: `${i * 0.05}s` }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {review.employee_photo ? (
                            <img src={review.employee_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                              <User className="w-5 h-5 text-[#5B6472]" />
                            </div>
                          )}
                          <div>
                            <h3 className="font-medium text-[#0A0F1E]">{review.employee_name || 'Employee'}</h3>
                            <p className={`text-sm capitalize ${reviewTypeColors[review.review_type] || 'text-blue-400'}`}>
                              {review.review_type?.replace('-', ' ')} Review
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#0A0F1E]">
                            {new Date(review.due_date).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })}
                          </p>
                          <p className="text-[#5B6472] text-sm">{review.scheduled_time}</p>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Company Profile */}
          <TabsContent value="profile" className="mt-6">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
              <div className="flex items-start gap-6">
                {companyInfo?.logo_url ? (
                  <img src={companyInfo.logo_url} alt="" className="w-20 h-20 rounded-xl object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-[#F5F8FF] flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-[#5B6472]" />
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-[#0A0F1E]">{companyInfo?.company_name || 'Company Name'}</h2>
                  {companyInfo?.industry && (
                    <p className="text-[#5B6472] mt-1">{companyInfo.industry}</p>
                  )}
                  {companyInfo?.company_size && (
                    <p className="text-[#5B6472] text-sm mt-1">{companyInfo.company_size} employees</p>
                  )}
                </div>
              </div>
              
              {companyInfo?.company_description && (
                <div className="mt-6 pt-6 border-t border-[#E8EDF5]">
                  <h3 className="text-[#5B6472] text-sm mb-2">About</h3>
                  <p className="text-[#0A0F1E]">{companyInfo.company_description}</p>
                </div>
              )}
              
              <div className="mt-6 pt-6 border-t border-[#E8EDF5] grid grid-cols-3 gap-4">
                <div>
                  <p className="text-[#5B6472] text-sm">Total Staff</p>
                  <p className="text-2xl font-bold text-[#0A0F1E]">{companyInfo?.staff_count || 0}</p>
                </div>
                <div>
                  <p className="text-[#5B6472] text-sm">VETT Records</p>
                  <p className="text-2xl font-bold text-[#0A0F1E]">{companyInfo?.record_count || 0}</p>
                </div>
                <div>
                  <p className="text-[#5B6472] text-sm">Verified Records</p>
                  <p className="text-2xl font-bold text-[#0A0F1E]">{companyInfo?.verified_record_count || 0}</p>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Invite Employee Modal */}
        <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#1B5AFF]" />
                Add Employee
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Full Name *</Label>
                <Input
                  value={inviteData.name}
                  onChange={(e) => setInviteData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Smith"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl"
                  data-testid="invite-name-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Work Email *</Label>
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@company.com"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl"
                  data-testid="invite-email-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Job Title *</Label>
                <Input
                  value={inviteData.job_title}
                  onChange={(e) => setInviteData(prev => ({ ...prev, job_title: e.target.value }))}
                  placeholder="Software Engineer"
                  className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl"
                  data-testid="invite-title-input"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#0A0F1E]">Department</Label>
                  <Input
                    value={inviteData.department}
                    onChange={(e) => setInviteData(prev => ({ ...prev, department: e.target.value }))}
                    placeholder="Engineering"
                    className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl"
                    data-testid="invite-department-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#0A0F1E]">Start Date</Label>
                  <Input
                    type="date"
                    value={inviteData.start_date}
                    onChange={(e) => setInviteData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl"
                    data-testid="invite-startdate-input"
                  />
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowInviteModal(false)}
                  className="text-[#5B6472]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={inviteLoading}
                  className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
                  data-testid="send-invite-btn"
                >
                  {inviteLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Invitation
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Company;
